import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner, ProgressBar, Badge } from 'react-bootstrap';
import { colors } from '../brand-colors';

/**
 * SalesPageAnalyzer - Public lead generation tool
 * No authentication required. Analyzes sales pages against conversion criteria.
 */
function SalesPageAnalyzer() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    sales_page_url: ''
  });

  const [results, setResults] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    if (!formData.sales_page_url.trim()) {
      setError('Please enter your sales page URL');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/sales-page-analyzer/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setResults(data);
        setSubmitted(true);
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Failed to analyze. Please check the URL and try again.');
    }

    setLoading(false);
  };

  const getGradeColor = (grade) => {
    switch (grade) {
      case 'A': return '#28a745';
      case 'B': return '#5cb85c';
      case 'C': return '#f0ad4e';
      case 'D': return '#d9534f';
      case 'F': return '#c9302c';
      default: return '#6c757d';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'danger';
  };

  // Results view
  if (submitted && results) {
    return (
      <Container className="py-5" style={{ maxWidth: '900px' }}>
        {/* Analyzed URL */}
        <Alert variant="secondary" className="mb-4 text-center">
          <small className="text-muted">Analyzed:</small>{' '}
          <a href={formData.sales_page_url} target="_blank" rel="noopener noreferrer">
            {formData.sales_page_url}
          </a>
        </Alert>

        {/* Header with Grade */}
        <Card className="shadow-lg mb-4 text-center" style={{ borderTop: `5px solid ${getGradeColor(results.grade)}` }}>
          <Card.Body className="py-5">
            <h1 className="mb-2">Your Sales Page Score</h1>
            <div
              style={{
                fontSize: '120px',
                fontWeight: '900',
                color: getGradeColor(results.grade),
                lineHeight: 1
              }}
            >
              {results.grade}
            </div>
            <h2 className="mt-3" style={{ color: colors.greyDark }}>
              {results.overall_score}/100 Points
            </h2>
            <p className="text-muted mb-0">
              {results.grade === 'A' && "Excellent! Your sales page hits most conversion best practices."}
              {results.grade === 'B' && "Good job! Your page is solid but has room for improvement."}
              {results.grade === 'C' && "Average. Several key elements are missing or weak."}
              {results.grade === 'D' && "Below average. Your page needs significant improvements."}
              {results.grade === 'F' && "Critical issues found. Major overhaul recommended."}
            </p>
          </Card.Body>
        </Card>

        {/* Section Breakdown */}
        <Card className="shadow-sm mb-4">
          <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
            <h5 className="mb-0">Score Breakdown</h5>
          </Card.Header>
          <Card.Body>
            {Object.entries(results.section_scores).map(([section, data]) => (
              <div key={section} className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span>{section}</span>
                  <span>
                    <Badge bg={getScoreColor(data.score)}>{data.score}%</Badge>
                    <small className="text-muted ms-2">({data.weight})</small>
                  </span>
                </div>
                <ProgressBar
                  now={data.score}
                  variant={getScoreColor(data.score)}
                  style={{ height: '8px' }}
                />
              </div>
            ))}
          </Card.Body>
        </Card>

        <Row>
          {/* Strengths */}
          <Col md={6} className="mb-4">
            <Card className="shadow-sm h-100" style={{ borderLeft: '4px solid #28a745' }}>
              <Card.Header style={{ backgroundColor: '#e8f5e9' }}>
                <h6 className="mb-0" style={{ color: '#28a745' }}>What You're Doing Well</h6>
              </Card.Header>
              <Card.Body>
                {results.strengths && results.strengths.length > 0 ? (
                  <ul className="mb-0">
                    {results.strengths.map((item, idx) => (
                      <li key={idx} className="mb-2">{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">No major strengths detected</p>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Weaknesses */}
          <Col md={6} className="mb-4">
            <Card className="shadow-sm h-100" style={{ borderLeft: '4px solid #dc3545' }}>
              <Card.Header style={{ backgroundColor: '#fce4ec' }}>
                <h6 className="mb-0" style={{ color: '#dc3545' }}>What's Missing or Weak</h6>
              </Card.Header>
              <Card.Body>
                {results.weaknesses && results.weaknesses.length > 0 ? (
                  <ul className="mb-0">
                    {results.weaknesses.map((item, idx) => (
                      <li key={idx} className="mb-2">{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">No major weaknesses detected</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Quick Wins */}
        {results.quick_wins && results.quick_wins.length > 0 && (
          <Card className="shadow-sm mb-4" style={{ borderLeft: '4px solid #ffc107' }}>
            <Card.Header style={{ backgroundColor: '#fff8e1' }}>
              <h6 className="mb-0" style={{ color: '#ff8f00' }}>Quick Wins - Easy Improvements</h6>
            </Card.Header>
            <Card.Body>
              <ol className="mb-0">
                {results.quick_wins.map((item, idx) => (
                  <li key={idx} className="mb-2">{item}</li>
                ))}
              </ol>
            </Card.Body>
          </Card>
        )}

        {/* Critical Issues */}
        {results.critical_issues && results.critical_issues.length > 0 && (
          <Card className="shadow-sm mb-4" style={{ borderLeft: '4px solid #c9302c' }}>
            <Card.Header style={{ backgroundColor: '#ffebee' }}>
              <h6 className="mb-0" style={{ color: '#c9302c' }}>Critical Issues to Address</h6>
            </Card.Header>
            <Card.Body>
              <ul className="mb-0">
                {results.critical_issues.map((item, idx) => (
                  <li key={idx} className="mb-2 text-danger">{item}</li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        )}

        {/* CTA for low scorers */}
        {results.needs_help && (
          <Card className="shadow-lg" style={{ backgroundColor: colors.greyDarkest, color: 'white' }}>
            <Card.Body className="text-center py-5">
              <h3 className="mb-3" style={{ color: colors.goldLight }}>
                Your Sales Page Needs Work
              </h3>
              <p className="mb-4" style={{ fontSize: '1.1rem' }}>
                A {results.grade}-grade sales page is costing you conversions every day.
                The good news? You don't have to figure this out alone.
              </p>
              <p className="mb-4">
                <strong>AI Profit Engineer</strong> can generate a complete, conversion-optimized
                sales page based on proven neuropersuasion principles - using YOUR unique story,
                features, and benefits.
              </p>
              <Button
                size="lg"
                href="/register"
                style={{
                  backgroundColor: colors.goldMid,
                  borderColor: colors.goldMid,
                  color: colors.greyDarkest,
                  fontWeight: 'bold',
                  padding: '1rem 3rem'
                }}
              >
                Get Your AI-Optimized Sales Page
              </Button>
              <p className="mt-3 mb-0 small" style={{ color: colors.greyLight }}>
                Free to start. No credit card required.
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Good scorers - still upsell but softer */}
        {!results.needs_help && (
          <Card className="shadow-sm" style={{ backgroundColor: '#e8f5e9' }}>
            <Card.Body className="text-center py-4">
              <h4 className="mb-3" style={{ color: '#2e7d32' }}>
                Nice Work! Want to Take It Further?
              </h4>
              <p className="mb-3">
                Your page is performing well, but there's always room for optimization.
                AI Profit Engineer can help you refine your messaging, add persuasion triggers,
                and create variations for A/B testing.
              </p>
              <Button
                variant="success"
                href="/register"
              >
                Explore AI Profit Engineer
              </Button>
            </Card.Body>
          </Card>
        )}

        {/* Analyze Another */}
        <div className="text-center mt-4">
          <Button
            variant="outline-secondary"
            onClick={() => {
              setSubmitted(false);
              setResults(null);
              setFormData({ name: '', email: '', sales_page_url: '' });
            }}
          >
            Analyze Another Page
          </Button>
        </div>
      </Container>
    );
  }

  // Form view
  return (
    <Container className="py-5" style={{ maxWidth: '600px' }}>
      <div className="text-center mb-5">
        <h1 style={{ color: colors.greyDarkest }}>
          Free Sales Page Analyzer
        </h1>
        <p className="lead text-muted">
          Get an instant conversion score and actionable recommendations
        </p>
      </div>

      <Card className="shadow-lg">
        <Card.Body className="p-4">
          {error && (
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-4">
              <Form.Label><strong>Your Name</strong></Form.Label>
              <Form.Control
                type="text"
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                disabled={loading}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label><strong>Email Address</strong></Form.Label>
              <Form.Control
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                disabled={loading}
              />
              <Form.Text className="text-muted">
                We'll send a copy of your results to this email.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label><strong>Sales Page URL</strong></Form.Label>
              <Form.Control
                type="url"
                placeholder="https://yoursite.com/sales-page"
                value={formData.sales_page_url}
                onChange={(e) => handleChange('sales_page_url', e.target.value)}
                disabled={loading}
              />
              <Form.Text className="text-muted">
                Enter the full URL of the sales page you want to analyze.
              </Form.Text>
            </Form.Group>

            <div className="d-grid">
              <Button
                type="submit"
                size="lg"
                disabled={loading}
                style={{
                  backgroundColor: colors.greyDarkest,
                  borderColor: colors.greyDarkest
                }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Analyzing Your Page... (30-60 seconds)
                  </>
                ) : (
                  'Analyze My Sales Page'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <div className="text-center mt-4">
        <p className="text-muted small">
          We analyze 50+ conversion factors including headlines, CTAs, social proof,
          persuasion triggers, and more.
        </p>
      </div>

      {/* What We Check */}
      <Card className="mt-4 shadow-sm">
        <Card.Body>
          <h5 className="mb-3" style={{ color: colors.greyDarkest }}>What We Analyze</h5>
          <Row>
            <Col xs={6}>
              <ul className="small mb-0">
                <li>Headlines & subheads</li>
                <li>Call-to-action buttons</li>
                <li>Video content</li>
                <li>Testimonials</li>
                <li>Guarantee messaging</li>
              </ul>
            </Col>
            <Col xs={6}>
              <ul className="small mb-0">
                <li>Social proof elements</li>
                <li>Trust badges</li>
                <li>FAQ section</li>
                <li>Urgency/scarcity</li>
                <li>Legal compliance</li>
              </ul>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default SalesPageAnalyzer;
